import { Button, Container, Group, Stack, Text, Title } from '@mantine/core';
import { useRouteError } from 'react-router-dom';

/**
 * RouteError — "this page broke", rendered INSIDE the authenticated shell so
 * the nav rail, the header and every other way out survive.
 *
 * Shell: (desktop) — it mounts in `DesktopLayout`'s outlet, and that is the
 * whole point. Never used by `(kiosk)`, which has its own catch-all.
 *
 * ## Why this exists (vendor#586)
 *
 * `router.tsx` declared exactly ONE `errorElement`, on the root `/` route —
 * the route whose `element` IS the shell. React Router replaces the erroring
 * route's `element` with its `errorElement`, so a render error thrown by ANY
 * child route unmounted `DesktopLayout` itself and replaced the entire screen:
 * nav rail, header, account switcher, all of it. One bad report page read as
 * "the app is broken", and the only affordance left was whatever the fallback
 * itself offered.
 *
 * The fix is a **pathless layout route** wrapping every child, carrying this
 * `errorElement`. An error bubbles to the nearest ancestor that declares one,
 * so it now stops one level below the shell — the outlet is replaced, the
 * shell is not.
 *
 * ## Two boundaries, not N, and not per-section
 *
 * vendor#586 raised the granularity as an open question — per top-level
 * section, or one at the layout. Per-section would be the same component
 * mounted N times with no per-section behaviour, which is an invented
 * distinction and N places to keep in step. So:
 *
 * - **Inner (this one), pathless, inside the shell.** Every child route. The
 *   shell survives, so the user still has the whole app to navigate to.
 * - **Outer, the root `/` route's existing `<NotFound />`.** Still there, and
 *   still necessary: it catches anything thrown by `RequireAuth`,
 *   `RequireSubscription` or `DesktopLayout` itself — cases where there is no
 *   shell left to preserve, because the shell is what failed.
 *
 * ## The escape is a real document navigation, and it stays that way
 *
 * `NotFound`'s docblock records that a React Router `<Link>` rendered BY an
 * active error boundary is not a reliable way out. Verified against the
 * installed `react-router@6.30.3`: `RenderErrorBoundary.getDerivedStateFromProps`
 * DOES clear the error when `state.location !== props.location`, so a
 * client-side navigation resets the boundary in the ordinary case — but the
 * cause that actually reached production was a chunk-load failure
 * (`lib/lazyWithReload.ts` exhausting its recovery), and a client-side
 * navigation to another lazily-loaded route can fail the same way. A full page
 * load always escapes. Both buttons here are `component="a"` for that reason.
 *
 * The rail is the reason this component can be quiet: it does not need to
 * offer a way to every part of the app, because the app is still on screen.
 */
export function RouteError() {
  const error = useRouteError();
  const detail = error instanceof Error ? error.message : null;

  return (
    <Container size="sm" py="xl" data-testid="route-error">
      <Stack align="center" gap="md">
        <Title order={2}>We couldn&apos;t load this page</Title>
        <Text c="dimmed" ta="center">
          The rest of the app is still working — use the nav to go somewhere else, or reload to try
          this page again.
        </Text>
        {detail && (
          <Text size="xs" c="dimmed" ta="center" style={{ wordBreak: 'break-word' }}>
            {detail}
          </Text>
        )}
        <Group gap="sm">
          <Button component="a" href={`${window.location.pathname}${window.location.search}`}>
            Reload this page
          </Button>
          <Button component="a" href="/" variant="default">
            Back to dashboard
          </Button>
        </Group>
      </Stack>
    </Container>
  );
}
